import React, { useEffect, useCallback } from 'react';
import {
  useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import LearningPathway from 'src/components/learning-pathway';
import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import { useAppSelector } from 'src/redux/hooks';
import { useGetLearningPathwayQuery, useGetLearningPathwayStaticQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import { selectLearningModuleExists } from 'src/redux/slices/learningModules';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import { AppState } from 'src/redux/store';
import ClientLogger from 'src/utils/logger';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { removeSearchParam } from 'src/utils/searchParams';
import { getStatusCodeFromError } from 'src/utils/error';

const LearningPathwayPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { learningPathwayId } = useParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const { viewMetric } = useMetrics();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const isPolyMigrationT1 = useAppSelector(
    (state: AppState) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration),
  );
  // Under T1, ungated users also call the PolyD learning-pathway API
  // instead of the static endpoint, for unified content delivery.
  const usePolyD = clientId !== UNGATED_CLIENT_ID || isPolyMigrationT1;

  if (!learningPathwayId) {
    const errorMessage = `Expected learningPathwayId to be present in browser URL path: ${location.pathname}`;
    const error = new Error(errorMessage);
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      path: location.pathname,
      url: window.location.href,
    });
    throw error;
  }

  // Call appropriate API based on client type (skip the other)
  const polyDResult = useGetLearningPathwayQuery(
    { learningPathwayId, clientId, selLocale },
    { skip: !usePolyD },
  );
  const staticResult = useGetLearningPathwayStaticQuery(
    { learningPathwayId, clientId, selLocale },
    { skip: usePolyD },
  );
  const { isLoading, isError, error } = usePolyD ? polyDResult : staticResult;

  // Track per-page performance (latency + availability) once the pathway finishes loading.
  // learningPathwayId is the resource key so client-side navigation between pathways (which reuses
  // this component instance) resets the measurement and re-emits metrics for each pathway.
  usePagePerformance(FeatureName.LearningPathwayPage, isLoading, isError, learningPathwayId);

  const learningModuleExistsInStore = useAppSelector((
    state: AppState,
  ) => selectLearningModuleExists(state, learningModuleId));

  // Use useEffect to track page view after component mounts
  useEffect(() => {
    if (!isLoading && !isError) {
      viewMetric({
        feature: FeatureName.LearningPathwayPage,
        metadata: {
          pathwayId: learningPathwayId,
          clientId,
          path: location.pathname,
        },
      });
    }
  }, [isLoading, isError, viewMetric, learningPathwayId, clientId, location.pathname]);

  const appendModuleSearchParam = (moduleId: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, moduleId);
    navigate({ search: updatedSearchParams.toString() }, { replace: true });
  };

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  if (isLoading) {
    return <LoadingView />;
  }

  if (isError) {
    const statusCode = getStatusCodeFromError(error);
    const logContext = {
      learningPathwayId,
      clientId,
      path: location.pathname,
      url: window.location.href,
    };
    if (statusCode !== undefined && statusCode >= 400 && statusCode < 500) {
      ClientLogger.warn(ClientLog.IllegalInternalDataState, logContext);
    } else {
      ClientLogger.error(ClientLog.IllegalInternalDataState, logContext);
    }
    return <ErrorView statusCode={statusCode} />;
  }

  return (
    <div>
      <LearningPathway learningPathwayId={learningPathwayId} onModuleSelect={appendModuleSearchParam} />
      {learningModuleId && learningModuleExistsInStore && (
      <LearningModuleModal
        learningModuleId={learningModuleId}
        onModalClose={removeModuleSearchParam}
        parentId={learningPathwayId}
        parentType={EntityType.LearningPathway}
      />
      )}
    </div>
  );
};

export default LearningPathwayPage;
